import { ItemConditionEnum, ItemTraceabilityGroup, ProductTraceabilityGroupUuid, ProductUuid, Stock, WarehouseLocation } from '@conveyr/shared-types';
import { AxiosInstance } from 'axios';
import type { components, operations } from '@/api/generated/openapi';
import { axiosWithoutCaseMiddleware } from '@/helpers/forms/axios';
import { StockResponse, toWarehouseLocation } from './stocks';

// The groups nested in the raw item that other modules receive. Like
// ShowInventoryItem, they serialise the raw model, not ProductTraceabilityGroup.
export interface ItemTraceabilityGroupResponse {
    uuid: string
    created_at: string
    updated_at: string
    deleted_at: string | null
    product_uuid: string
    code: string
    description: string | null
    is_unsellable: boolean
    condition: ItemConditionEnum | null
    batch_code: string | null
    serial_number: string | null
    expiry_date: string | null
    stocks: Array<StockResponse> | undefined
}

export type EmbeddedTraceabilityGroupRaw = NonNullable<components['schemas']['InventoryItem']['traceability_groups']>[number];

export type TraceabilityGroupRaw = components['schemas']['ProductTraceabilityGroup'];
export type TraceabilityGroupStockRaw = TraceabilityGroupRaw['stocks'][number];

export type FetchTraceabilityGroupsResponse = operations['index-product-traceability-groups']['responses']['200']['content']['application/json'];
export type CreateTraceabilityGroupResponse = WithStockLocations<operations['create-product-traceability-group']['responses']['201']['content']['application/json']>;
export type UpdateTraceabilityGroupResponse = WithStockLocations<operations['update-product-traceability-group']['responses']['200']['content']['application/json']>;
export type DeleteTraceabilityGroupResponse = operations['delete-product-traceability-group']['responses']['204']['content'];

export type CreateTraceabilityGroupRequest = operations['create-product-traceability-group']['requestBody']['content']['application/json'];
export type UpdateTraceabilityGroupRequest = operations['update-product-traceability-group']['requestBody']['content']['application/json'];

type TraceabilityGroupLocationRaw = NonNullable<TraceabilityGroupStockRaw['location']>;
type TraceabilityGroupStockWithLocationRaw = Omit<TraceabilityGroupStockRaw, 'location'> & { location: TraceabilityGroupLocationRaw };
type TraceabilityGroupWithLocationsRaw = Omit<TraceabilityGroupRaw, 'stocks'> & { stocks: TraceabilityGroupStockWithLocationRaw[] };

// ProductTraceabilityGroup is shared with the operations whose stock rows omit
// `location`, so it declares it optional. Update is documented with "The
// returned group's `stocks` rows include `location`", and create with "A new
// group has no stock, so the returned group's `stocks` is empty".
type WithStockLocations<Response> = {
    [Key in keyof Response]: Response[Key] extends TraceabilityGroupRaw ? TraceabilityGroupWithLocationsRaw : Response[Key]
};

export interface ItemTraceabilityGroupCreateForm {
    code: string
    description: string | null
    isUnsellable: boolean
    condition: ItemConditionEnum | null
    batchCode?: string | null
    serialNumber?: string | null
    expiryDate?: string | null
}

export interface ItemTraceabilityGroupUpdateForm {
    code: string
    description: string | null
    isUnsellable: boolean
    condition: ItemConditionEnum | null
    batchCode: string | null
    serialNumber: string | null
    expiryDate: string | null
}

function toTraceabilityGroupStockFields(row: TraceabilityGroupStockRaw | StockResponse): Omit<Stock<undefined, undefined>, 'location'> {
    return {
        quantity: row.quantity,
        quantityInStock: row.quantity_in_stock,
        quantityExpected: row.quantity_expected,
        quantityReserved: row.quantity_reserved,
        quantityFree: row.quantity_free,
        locationUuid: row.location_uuid,
        productUuid: row.product_uuid,
        productTraceabilityGroupUuid: row.product_traceability_group_uuid,
        item: undefined,
    };
}

// Also maps the stock rows of the groups embedded in an inventory item.
export function toTraceabilityGroupStock(row: TraceabilityGroupStockRaw | StockResponse): Stock<WarehouseLocation | undefined, undefined> {
    return {
        ...toTraceabilityGroupStockFields(row),
        location: row.location === undefined ? undefined : toWarehouseLocation(row.location),
    };
}

function toTraceabilityGroupStockWithLocation(row: TraceabilityGroupStockWithLocationRaw): Stock<WarehouseLocation, undefined> {
    return {
        ...toTraceabilityGroupStockFields(row),
        location: toWarehouseLocation(row.location),
    };
}

function toTraceabilityGroupFields(
    group: TraceabilityGroupRaw | TraceabilityGroupWithLocationsRaw | EmbeddedTraceabilityGroupRaw | ItemTraceabilityGroupResponse,
): Omit<ItemTraceabilityGroup, 'stocks'> {
    return {
        uuid: group.uuid,
        createdAt: group.created_at,
        updatedAt: group.updated_at,
        productUuid: group.product_uuid,
        code: group.code,
        description: group.description,
        isUnsellable: group.is_unsellable,
        condition: group.condition,
        batchCode: group.batch_code,
        serialNumber: group.serial_number,
        expiryDate: group.expiry_date,
        isDeleted: 'is_deleted' in group ? group.is_deleted : group.deleted_at !== null,
    };
}

// The embedded groups' stocks may be absent, so the stocks of the result are
// too.
export function toItemTraceabilityGroup(
    group: TraceabilityGroupRaw | EmbeddedTraceabilityGroupRaw | ItemTraceabilityGroupResponse,
): ItemTraceabilityGroup<Stock<WarehouseLocation | undefined, undefined> | undefined> {
    return {
        ...toTraceabilityGroupFields(group),
        stocks: group.stocks?.map(toTraceabilityGroupStock),
    };
}

function toTraceabilityGroupWithLocations(group: TraceabilityGroupWithLocationsRaw): ItemTraceabilityGroup<Stock<WarehouseLocation, undefined>> {
    return {
        ...toTraceabilityGroupFields(group),
        stocks: group.stocks.map(toTraceabilityGroupStockWithLocation),
    };
}

export default (axios: AxiosInstance = axiosWithoutCaseMiddleware): { fetchItemTraceabilityGroups: (productUuid: ProductUuid) => Promise<ItemTraceabilityGroup[]>; createTraceabilityGroup: (productUuid: ProductUuid, formData: ItemTraceabilityGroupCreateForm) => Promise<ItemTraceabilityGroup<Stock<WarehouseLocation>>>; updateTraceabilityGroup: (productUuid: ProductUuid, productTraceabilityGroupUuid: ProductUuid, formData: ItemTraceabilityGroupUpdateForm) => Promise<ItemTraceabilityGroup<Stock<WarehouseLocation>>>; deleteTraceabilityGroup: (productUuid: ProductUuid, productTraceabilityGroupUuid: ProductTraceabilityGroupUuid) => Promise<void>; } => ({
    fetchItemTraceabilityGroups: async (productUuid: ProductUuid): Promise<ItemTraceabilityGroup[]> => {
        const response = await axios.get<FetchTraceabilityGroupsResponse>(`/api/inventory/products/${productUuid}/traceability-groups`);

        return response.data.data.map(toItemTraceabilityGroup);
    },

    createTraceabilityGroup: async (productUuid: ProductUuid, formData: ItemTraceabilityGroupCreateForm): Promise<ItemTraceabilityGroup<Stock<WarehouseLocation>>> => {
        const body: CreateTraceabilityGroupRequest = {
            code: formData.code,
            description: formData.description,
            is_unsellable: formData.isUnsellable,
            condition: formData.condition,
            batch_code: formData.batchCode ?? null,
            serial_number: formData.serialNumber ?? null,
            expiry_date: formData.expiryDate ?? null,
        };
        const response = await axios.post<CreateTraceabilityGroupResponse>(`/api/inventory/products/${productUuid}/traceability-groups`, body);

        return toTraceabilityGroupWithLocations(response.data.data);
    },

    updateTraceabilityGroup: async (
        productUuid: ProductUuid,
        productTraceabilityGroupUuid: ProductUuid,
        formData: ItemTraceabilityGroupUpdateForm,
    ): Promise<ItemTraceabilityGroup<Stock<WarehouseLocation>>> => {
        const body: UpdateTraceabilityGroupRequest = {
            code: formData.code,
            description: formData.description,
            is_unsellable: formData.isUnsellable,
            condition: formData.condition,
            batch_code: formData.batchCode,
            serial_number: formData.serialNumber,
            expiry_date: formData.expiryDate,
        };
        const response = await axios.put<UpdateTraceabilityGroupResponse>(`/api/inventory/products/${productUuid}/traceability-groups/${productTraceabilityGroupUuid}`, body);

        return toTraceabilityGroupWithLocations(response.data.data);
    },

    deleteTraceabilityGroup: async (productUuid: ProductUuid, productTraceabilityGroupUuid: ProductTraceabilityGroupUuid): Promise<void> => {
        await axios.delete<DeleteTraceabilityGroupResponse>(`/api/inventory/products/${productUuid}/traceability-groups/${productTraceabilityGroupUuid}`);
    },
});
